import type { ActivityContext, ActivityHandle } from '../core/activity';
import { mountKeypad } from './keypad';

export type VerticalOperation = 'plus' | 'minus';

interface RawRoundConfig {
  nbSubLevel?: number;
  nbDigits?: number;
  nbLines?: number;
  alreadyLaid?: boolean;
  withCarry?: boolean;
}

interface RawLevel {
  level: number;
  objective: string;
  difficulty: number;
  data: RawRoundConfig[];
}

interface RoundConfig {
  nbSubLevel: number;
  nbDigits: number;
  nbLines: number;
  withCarry: boolean;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: RoundConfig[];
}

export function filterLaidLevels(raw: RawLevel[]): Level[] {
  const out: Level[] = [];
  let next = 1;
  for (const lvl of raw) {
    const data: RoundConfig[] = [];
    for (const r of lvl.data) {
      if (r.alreadyLaid === false) continue;
      if (r.nbDigits === undefined || r.nbLines === undefined) continue;
      data.push({
        nbSubLevel: r.nbSubLevel ?? 5,
        nbDigits: r.nbDigits,
        nbLines: r.nbLines,
        withCarry: r.withCarry ?? false,
      });
    }
    if (data.length === 0) continue;
    out.push({ level: next++, objective: lvl.objective, difficulty: lvl.difficulty, data });
  }
  return out;
}

interface VerticalRunOptions {
  operation: VerticalOperation;
  title: string;
  activityId: string;
  levels: Level[];
}

export function runVerticalActivity(ctx: ActivityContext, opts: VerticalRunOptions): ActivityHandle {
  const { operation, title, activityId, levels: LEVELS } = opts;
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const opSign = operation === 'plus' ? '+' : '−';
  let subIndex = 0;
  let operands: number[] = [];
  let expected = 0;
  let answer = '';
  let correctCount = 0;
  const totalSubs = level.data[0]?.nbSubLevel ?? 5;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">${title}</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage vertical">
      <div class="feedback" aria-live="polite"></div>
      <p class="vertical__progress" aria-live="polite"></p>
      <div class="vertical__operation"></div>
      <div class="vertical__answer-wrap">
        <span class="vertical__answer-label">Resposta:</span>
        <div class="vertical__answer" aria-live="polite">?</div>
      </div>
      <div class="vertical__keypad-wrap"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.vertical__progress')!;
  const opEl = page.querySelector<HTMLElement>('.vertical__operation')!;
  const answerEl = page.querySelector<HTMLElement>('.vertical__answer')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const keypadWrap = page.querySelector<HTMLElement>('.vertical__keypad-wrap')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  mountKeypad(keypadWrap, {
    onDigit: (d) => {
      const maxLen = level.data[0]!.nbDigits + 1;
      if (answer.length < maxLen) answer += d;
      renderAnswer();
    },
    onBackspace: () => {
      answer = answer.slice(0, -1);
      renderAnswer();
    },
    onEnter: validate,
  });

  const onKey = (e: KeyboardEvent): void => {
    const maxLen = level.data[0]!.nbDigits + 1;
    if (e.key >= '0' && e.key <= '9' && answer.length < maxLen) { answer += e.key; renderAnswer(); }
    else if (e.key === 'Backspace') { answer = answer.slice(0, -1); renderAnswer(); }
    else if (e.key === 'Enter') validate();
    else return;
    e.preventDefault();
  };
  window.addEventListener('keydown', onKey);

  function startRound(): void {
    const cfg = level.data[0]!;
    operands = generateOperands(cfg, operation);
    expected = operation === 'plus'
      ? operands.reduce((a, b) => a + b, 0)
      : operands.reduce((a, b, i) => i === 0 ? a + b : a - b, 0);
    answer = '';
    renderProgress();
    renderOperation();
    renderAnswer();
  }

  function renderProgress(): void {
    progressEl.textContent = `Rodada ${subIndex + 1} de ${totalSubs}`;
  }

  function renderOperation(): void {
    opEl.innerHTML = '';
    const maxDigits = Math.max(level.data[0]!.nbDigits, String(expected).length);
    operands.forEach((n, i) => {
      const row = document.createElement('div');
      row.className = 'vertical__row';
      const sign = document.createElement('span');
      sign.className = 'vertical__sign';
      sign.textContent = i === operands.length - 1 ? opSign : ' ';
      row.appendChild(sign);
      const digits = String(n).padStart(maxDigits, ' ');
      for (const d of digits) {
        const cell = document.createElement('span');
        cell.className = 'vertical__cell';
        cell.textContent = d;
        row.appendChild(cell);
      }
      opEl.appendChild(row);
    });
    const line = document.createElement('div');
    line.className = 'vertical__line';
    opEl.appendChild(line);
    const resultRow = document.createElement('div');
    resultRow.className = 'vertical__row vertical__row--result';
    const blank = document.createElement('span');
    blank.className = 'vertical__sign';
    blank.textContent = ' ';
    resultRow.appendChild(blank);
    for (let i = 0; i < maxDigits; i++) {
      const cell = document.createElement('span');
      cell.className = 'vertical__cell vertical__cell--result';
      cell.textContent = ' ';
      resultRow.appendChild(cell);
    }
    opEl.appendChild(resultRow);
  }

  function renderAnswer(): void {
    answerEl.textContent = answer === '' ? '?' : answer;
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    if (answer === '') return;
    const value = parseInt(answer, 10);
    if (value === expected) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < totalSubs) startRound();
        else finishLevel();
      }, 600);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      answer = '';
      window.setTimeout(renderAnswer, 500);
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalSubs} contas.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/${activityId}/${level.level + 1}`;
      });
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function generateOperands(cfg: RoundConfig, operation: VerticalOperation): number[] {
  const min = Math.pow(10, cfg.nbDigits - 1);
  const max = Math.pow(10, cfg.nbDigits) - 1;
  for (let attempt = 0; attempt < 50; attempt++) {
    const nums: number[] = [];
    for (let i = 0; i < cfg.nbLines; i++) {
      nums.push(randInt(min, max));
    }
    if (operation === 'plus') {
      if (!cfg.withCarry && hasCarry(nums)) continue;
      return nums;
    }
    // minus: ordenar do maior pro menor, garantir resultado >= 0
    nums.sort((a, b) => b - a);
    const first = nums[0]!;
    const rest = nums.slice(1).reduce((a, b) => a + b, 0);
    if (first < rest) continue;
    if (!cfg.withCarry && hasBorrow(nums)) continue;
    return nums;
  }
  // fallback
  if (operation === 'plus') return new Array(cfg.nbLines).fill(min);
  const fallback = new Array(cfg.nbLines).fill(min);
  fallback[0] = max;
  return fallback;
}

function hasCarry(nums: number[]): boolean {
  const digits = nums.map((n) => String(n).split('').map(Number));
  const maxLen = Math.max(...digits.map((d) => d.length));
  for (let col = 0; col < maxLen; col++) {
    let sum = 0;
    for (const d of digits) {
      const idx = d.length - 1 - col;
      if (idx >= 0) sum += d[idx]!;
    }
    if (sum >= 10) return true;
  }
  return false;
}

function hasBorrow(nums: number[]): boolean {
  // ordenado decrescente: nums[0] - sum(rest)
  const first = nums[0]!;
  const rest = nums.slice(1);
  const firstDigits = String(first).split('').map(Number);
  const restDigits = rest.map((n) => String(n).padStart(firstDigits.length, '0').split('').map(Number));
  for (let col = firstDigits.length - 1; col >= 0; col--) {
    const top = firstDigits[col]!;
    const sub = restDigits.reduce((a, d) => a + d[col]!, 0);
    if (top < sub) return true;
  }
  return false;
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
